import { stripBasePath } from './basePath'

export type RouteId =
  | 'root'
  | 'welcome'
  | 'register'
  | 'login'
  | 'recovery'
  | 'email-next-step'
  | 'onboarding-city'
  | 'onboarding-ticket'
  | 'home'
  | 'tour'
  | 'show-detail'
  | 'feed'
  | 'tickets'
  | 'ticket-connect'
  | 'ticket-detail'
  | 'itinerary'
  | 'check-in'
  | 'passport'
  | 'notifications'
  | 'content'
  | 'content-detail'
  | 'profile'
  | 'settings'
  | 'settings-accessibility'
  | 'settings-privacy'
  | 'settings-notifications'
  | 'support'
  | 'not-found'

export interface AppLocation {
  id: RouteId
  pathname: string
  search: string
  params: Record<string, string>
  protected: boolean
}

const publicRoutes = new Set<RouteId>([
  'root',
  'welcome',
  'register',
  'login',
  'recovery',
  'tour',
  'show-detail',
  'content',
  'content-detail',
  'support',
])

const exactRoutes: Record<string, RouteId> = {
  '/': 'root',
  '/welcome': 'welcome',
  '/auth/create': 'register',
  '/auth/sign-in': 'login',
  '/auth/recovery': 'recovery',
  '/auth/email-next-step': 'email-next-step',
  '/onboarding/city': 'onboarding-city',
  '/onboarding/ticket': 'onboarding-ticket',
  '/home': 'home',
  '/tour': 'tour',
  '/feed': 'feed',
  '/tickets': 'tickets',
  '/tickets/connect': 'ticket-connect',
  '/itinerary': 'itinerary',
  '/passport': 'passport',
  '/notifications': 'notifications',
  '/content': 'content',
  '/profile': 'profile',
  '/settings': 'settings',
  '/settings/accessibility': 'settings-accessibility',
  '/settings/privacy': 'settings-privacy',
  '/settings/notifications': 'settings-notifications',
  '/support': 'support',
}

function cleanPath(pathname: string): string {
  if (!pathname || pathname === '/') return '/'
  return `/${pathname.split('/').filter(Boolean).join('/')}`
}

export function matchRoute(pathname: string, search = ''): AppLocation {
  const path = cleanPath(pathname)
  const exact = exactRoutes[path]
  if (exact) {
    return { id: exact, pathname: path, search, params: {}, protected: !publicRoutes.has(exact) }
  }

  const patterns: Array<{ expression: RegExp; id: RouteId; keys: string[] }> = [
    { expression: /^\/shows\/([^/]+)$/, id: 'show-detail', keys: ['showId'] },
    { expression: /^\/tickets\/([^/]+)$/, id: 'ticket-detail', keys: ['ticketId'] },
    { expression: /^\/check-in\/([^/]+)$/, id: 'check-in', keys: ['showId'] },
    { expression: /^\/content\/([^/]+)$/, id: 'content-detail', keys: ['contentId'] },
    {
      expression: /^\/community\/([^/]+)\/(main|updates|fits|merch|scene|energy)(?:\/post\/([^/]+))?$/,
      id: 'feed',
      keys: ['citySlug', 'channel', 'postId'],
    },
  ]

  for (const pattern of patterns) {
    const match = path.match(pattern.expression)
    if (!match) continue
    const params: Record<string, string> = {}
    pattern.keys.forEach((key, index) => {
      const value = match[index + 1]
      if (value) params[key] = decodeURIComponent(value)
    })
    return { id: pattern.id, pathname: path, search, params, protected: !publicRoutes.has(pattern.id) }
  }

  return { id: 'not-found', pathname: path, search, params: {}, protected: false }
}

export function readLocation(location: Pick<Location, 'pathname' | 'search'>): AppLocation {
  return matchRoute(stripBasePath(location.pathname), location.search)
}

export function safeReturnTo(value: string | null): string {
  if (!value || !value.startsWith('/') || value.includes('\\') || /[\u0000-\u001f\u007f]/.test(value)) return '/home'
  try {
    const base = 'https://paradox.local'
    const parsed = new URL(value, base)
    if (parsed.origin !== base) return '/home'
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return '/home'
  }
}

export function routeLabel(route: RouteId): string {
  const labels: Partial<Record<RouteId, string>> = {
    root: 'The Paradox',
    welcome: 'Welcome',
    register: 'Create account',
    login: 'Sign in',
    recovery: 'Account recovery',
    'email-next-step': 'Account next step',
    'onboarding-city': 'Choose a city',
    'onboarding-ticket': 'Connect a ticket',
    home: 'Home',
    tour: 'Tour',
    'show-detail': 'Show details',
    feed: 'Community',
    tickets: 'Tickets',
    'ticket-connect': 'Connect ticket',
    'ticket-detail': 'Ticket details',
    itinerary: 'Itinerary',
    'check-in': 'Check in',
    passport: 'Passport',
    notifications: 'Notifications',
    content: 'Tour Desk',
    'content-detail': 'Tour Desk',
    profile: 'Profile',
    settings: 'Settings',
    'settings-accessibility': 'Accessibility',
    'settings-privacy': 'Privacy',
    'settings-notifications': 'Notification settings',
    support: 'Support',
    'not-found': 'Page not found',
  }
  return labels[route] ?? 'The Paradox'
}
